import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Icon, SelectMenu, toast } from '@livable/saas-react-components';
import { useStore } from 'src/stores';
import { dateToFormat } from 'src/utils/date';
import { COUPON_STATUS, couponAllowsTarget, couponRedemptionKey, formatCouponDiscount, formatCouponDuration, getCouponStatus } from '../../couponsModel';
import useStyles from './styles';
const EMPTY_LIST = [];

/**
 * The account itself, as a target. A non-empty sentinel rather than null or '': the select renders
 * nothing for '' and would show only its "Applies to" label. Property ids are GUIDs, so it cannot clash.
 */
const ACCOUNT_TARGET = 'account';

/** Coupons keyed by their uppercased code, for the lookups the rows and validation do. */
function indexCouponsByCode(coupons) {
  const map = new Map();
  coupons.forEach(c => {
    var _c$code;
    return map.set(((_c$code = c.code) !== null && _c$code !== void 0 ? _c$code : '').toUpperCase(), c);
  });
  return map;
}

/** What the applied row says under the code, e.g. "100% off · 1 month". */
function describeCoupon(coupon) {
  if (!coupon) return '';
  return "".concat(formatCouponDiscount(coupon), " \xB7 ").concat(formatCouponDuration(coupon));
}

/**
 * The Coupon section of the subscription modal. In edit mode it applies and removes coupons
 * straight away; in create mode there is no subscription to hang them off yet, so codes are held
 * and handed to the parent to apply once the save succeeds.
 *
 * A coupon goes either on the whole account or on one of its properties, which is why every code
 * carries a target: the backend keys a redemption by company *and* property, and a property-scoped
 * coupon only discounts that property's share of the invoice.
 */
export const SubscriptionCoupons = observer(_ref => {
  var _store$stripe$coupons, _store$stripe$company;
  let companyId = _ref.companyId,
    _ref$properties = _ref.properties,
    properties = _ref$properties === void 0 ? EMPTY_LIST : _ref$properties,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$pendingCodes = _ref.pendingCodes,
    pendingCodes = _ref$pendingCodes === void 0 ? EMPTY_LIST : _ref$pendingCodes,
    onPendingChange = _ref.onPendingChange,
    _ref$isEdit = _ref.isEdit,
    isEdit = _ref$isEdit === void 0 ? false : _ref$isEdit;
  const classes = useStyles();
  const store = useStore();
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    code = _useState2[0],
    setCode = _useState2[1];
  const _useState3 = useState(ACCOUNT_TARGET),
    _useState4 = _slicedToArray(_useState3, 2),
    target = _useState4[0],
    setTarget = _useState4[1];
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    error = _useState6[0],
    setError = _useState6[1];

  // Both views hand back a fresh toJS array on every read, so memoising off them never hits.
  const coupons = (_store$stripe$coupons = store.stripe.couponsList) !== null && _store$stripe$coupons !== void 0 ? _store$stripe$coupons : EMPTY_LIST;
  const redemptions = (_store$stripe$company = store.stripe.companyRedemptionsList) !== null && _store$stripe$company !== void 0 ? _store$stripe$company : EMPTY_LIST;

  // What the request carries: the select holds ACCOUNT_TARGET for the account, the API wants null.
  const targetPropertyId = target === ACCOUNT_TARGET ? null : target;
  useEffect(() => {
    if (!store.stripe.couponsListLoaded) store.stripe.getCouponsAsync.run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (isEdit && companyId) {
      store.stripe.getCompanyCouponRedemptionsAsync.run({
        companyId
      });
    } else {
      store.stripe.clearCompanyRedemptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, companyId]);

  // A property that has gone away should not stay selected as a target.
  useEffect(() => {
    if (target !== ACCOUNT_TARGET && !properties.some(prop => prop.propertyId === target)) {
      setTarget(ACCOUNT_TARGET);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties]);
  const byCode = indexCouponsByCode(coupons);

  /** Which account or property a row is against, in the words Ops uses for it. */
  const describeTarget = propertyId => {
    if (propertyId == null) return 'Account-wide';
    const prop = properties.find(p => p.propertyId === propertyId);
    return (prop === null || prop === void 0 ? void 0 : prop.propertyAddress) || (prop === null || prop === void 0 ? void 0 : prop.propertyName) || "Property ".concat(String(propertyId).slice(0, 8));
  };

  /** Rows shown under the field: applied ones in edit mode, queued ones in create mode. */
  const rows = isEdit ? redemptions.map(r => {
    var _r$propertyId, _r$code;
    return {
      key: r.id,
      code: r.code,
      propertyId: (_r$propertyId = r.propertyId) !== null && _r$propertyId !== void 0 ? _r$propertyId : null,
      description: describeCoupon(byCode.get(((_r$code = r.code) !== null && _r$code !== void 0 ? _r$code : '').toUpperCase())),
      appliedOn: r.redeemedOn,
      expiresOn: r.expiresOn,
      isPending: false
    };
  }) : pendingCodes.map(entry => {
    var _entry$propertyId;
    return {
      key: couponRedemptionKey(entry.code, entry.propertyId),
      code: entry.code,
      propertyId: (_entry$propertyId = entry.propertyId) !== null && _entry$propertyId !== void 0 ? _entry$propertyId : null,
      description: describeCoupon(byCode.get(entry.code)),
      isPending: true
    };
  });

  /** Mirrors the backend's own refusals so Ops sees them before the round trip. */
  const validate = normalized => {
    const coupon = byCode.get(normalized);
    if (!coupon) return 'No coupon with this code.';
    if (getCouponStatus(coupon) !== COUPON_STATUS.ACTIVE) {
      return 'This coupon is a draft, expired or fully redeemed.';
    }
    if (!couponAllowsTarget(coupon, targetPropertyId == null)) {
      return targetPropertyId == null ? 'This coupon applies to properties only — pick a property above.' : 'This coupon applies to accounts only — set Applies to back to the account.';
    }
    // The same coupon may go on the account and on a property; only the exact pair is a duplicate.
    const applied = new Set(rows.map(row => {
      var _row$code;
      return couponRedemptionKey(((_row$code = row.code) !== null && _row$code !== void 0 ? _row$code : '').toUpperCase(), row.propertyId);
    }));
    if (applied.has(couponRedemptionKey(normalized, targetPropertyId))) {
      return 'This coupon is already applied to that target.';
    }
    return '';
  };
  const handleApply = async () => {
    const normalized = code.trim().toUpperCase();
    if (!normalized) return;
    const message = validate(normalized);
    if (message) {
      setError(message);
      return;
    }
    setError('');
    if (!isEdit) {
      onPendingChange === null || onPendingChange === void 0 || onPendingChange([...pendingCodes, {
        code: normalized,
        propertyId: targetPropertyId
      }]);
      setCode('');
      return;
    }
    await store.stripe.applyCouponAsync.run({
      code: normalized,
      companyId,
      propertyId: targetPropertyId
    });
    if (store.stripe.applyCouponAsync.isError) return;
    toast('success', {
      body: ['Coupon applied', normalized]
    });
    setCode('');
    await store.stripe.getCompanyCouponRedemptionsAsync.run({
      companyId
    });
  };
  const handleRemove = async row => {
    if (row.isPending) {
      onPendingChange === null || onPendingChange === void 0 || onPendingChange(pendingCodes.filter(entry => couponRedemptionKey(entry.code, entry.propertyId) !== couponRedemptionKey(row.code, row.propertyId)));
      return;
    }
    await store.stripe.revokeCouponRedemptionAsync.run({
      id: row.key
    });
    if (store.stripe.revokeCouponRedemptionAsync.isError) return;
    toast('success', {
      body: ['Coupon removed', row.code]
    });
    await store.stripe.getCompanyCouponRedemptionsAsync.run({
      companyId
    });
  };
  const isBusy = store.stripe.applyCouponAsync.inProgress || store.stripe.revokeCouponRedemptionAsync.inProgress;
  return /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    sx: {
      marginBottom: '12px'
    }
  }, "Coupon"), /*#__PURE__*/React.createElement(Box, {
    className: classes.applyRow
  }, /*#__PURE__*/React.createElement(TextField, {
    placeholder: "Coupon code",
    value: code,
    onChange: e => {
      setCode(e.target.value);
      if (error) setError('');
    },
    onKeyDown: e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleApply();
      }
    },
    disabled: disabled || isBusy,
    error: !!error,
    helperText: error,
    className: classes.codeField
  }), /*#__PURE__*/React.createElement(Box, {
    className: classes.targetField
  }, /*#__PURE__*/React.createElement(SelectMenu, {
    id: "coupon-target",
    labelId: "coupon-target-label",
    label: "Applies to",
    value: target,
    disabled: disabled || isBusy,
    onChange: e => {
      setTarget(e.target.value);
      if (error) setError('');
    }
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: ACCOUNT_TARGET
  }, "Account-wide"), properties.map(prop => /*#__PURE__*/React.createElement(MenuItem, {
    key: prop.propertyId,
    value: prop.propertyId
  }, prop.propertyAddress || prop.propertyName || prop.propertyId)))), /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    onClick: handleApply,
    disabled: disabled || isBusy || !code.trim()
  }, "Apply")), rows.length > 0 && /*#__PURE__*/React.createElement(Box, {
    className: classes.appliedList
  }, rows.map(row => /*#__PURE__*/React.createElement(Box, {
    key: row.key,
    className: classes.appliedRow
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.appliedText
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.code
  }, row.code), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, row.description || '—', row.isPending ? ' · applied once the subscription is saved' : row.expiresOn ? " \xB7 until ".concat(dateToFormat(row.expiresOn)) : ''), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary",
    className: classes.target
  }, describeTarget(row.propertyId))), /*#__PURE__*/React.createElement(IconButton, {
    "aria-label": "Remove coupon",
    onClick: () => handleRemove(row),
    disabled: isBusy
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "close"
  }))))));
});